import { useEffect, useState, useCallback } from "react";
import { Loader2, Megaphone, Save, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
const AD_STATUSES = ["pending_setup", "pending_review", "active", "needs_changes", "paused", "cancelled", "expired", "rejected"];

function Stat({ label, value }) {
  return <div className="bg-white border border-saddle/20 p-4" data-testid="ad-admin-stat"><span className="hr-overline text-saddle">{label}</span><p className="font-serif text-2xl text-forest mt-1">{value}</p></div>;
}

export function AdminAdvertising() {
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    api.adminAdvertising().then(setData).catch(() => {});
    api.adminAdProducts().then((d) => setProducts(d.products)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const editProduct = (key, field, val) => setProducts(products.map((p) => p.key === key ? { ...p, [field]: field === "active" ? val : Math.round(Number(val) * 100) } : p));
  const saveProduct = async (p) => {
    setSavingKey(p.key);
    try {
      await api.adminUpdateAdProduct(p.key, { monthly_cents: p.monthly_cents, annual_cents: p.annual_cents, one_time_cents: p.one_time_cents, active: p.active });
      toast.success(`${p.name} pricing saved`);
    } catch { toast.error("Could not save"); }
    finally { setSavingKey(null); }
  };

  const setStatus = async (ad, status) => {
    let admin_note;
    if (status === "needs_changes") { admin_note = window.prompt("What needs to change? (shown to advertiser)", ad.admin_note || ""); if (admin_note === null) return; }
    try { await api.adminSetAdStatus(ad.id, { status, admin_note }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="mt-10 py-10 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>;

  return (
    <div className="mt-12" data-testid="admin-advertising">
      <h2 className="font-serif text-2xl text-forest mb-3 flex items-center gap-2"><Megaphone size={20} /> Advertising</h2>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ad-admin-stats">
        <Stat label="Total ads" value={data.stats.total} />
        <Stat label="Active" value={data.stats.active} />
        <Stat label="Pending" value={data.stats.pending} />
        <Stat label="Monthly recurring" value={dollars(data.stats.mrr_cents)} />
      </div>

      {/* Product pricing */}
      <h3 className="font-serif text-lg text-forest mt-8 mb-2">Ad product pricing</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ad-product-editor">
        {products.map((p) => (
          <div key={p.key} className="bg-white border border-saddle/20 p-4" data-testid={`ad-prod-${p.key}`}>
            <div className="flex items-center justify-between">
              <strong className="text-forest text-sm">{p.name}</strong>
              <label className="text-[11px] flex items-center gap-1 text-charcoal/60"><input type="checkbox" checked={p.active} onChange={(e) => editProduct(p.key, "active", e.target.checked)} data-testid={`ad-active-${p.key}`} /> Active</label>
            </div>
            {p.billing_type === "recurring" ? (
              <>
                <label className="block text-xs text-charcoal/60 mt-2">Monthly ($)<input type="number" defaultValue={(p.monthly_cents / 100).toFixed(0)} onChange={(e) => editProduct(p.key, "monthly_cents", e.target.value)} className="w-full border border-saddle/25 px-2 py-1.5 mt-1 outline-none focus:border-forest" data-testid={`ad-monthly-${p.key}`} /></label>
                <label className="block text-xs text-charcoal/60 mt-2">Annual ($)<input type="number" defaultValue={(p.annual_cents / 100).toFixed(0)} onChange={(e) => editProduct(p.key, "annual_cents", e.target.value)} className="w-full border border-saddle/25 px-2 py-1.5 mt-1 outline-none focus:border-forest" data-testid={`ad-annual-${p.key}`} /></label>
              </>
            ) : (
              <label className="block text-xs text-charcoal/60 mt-2">One-time ($)<input type="number" defaultValue={(p.one_time_cents / 100).toFixed(0)} onChange={(e) => editProduct(p.key, "one_time_cents", e.target.value)} className="w-full border border-saddle/25 px-2 py-1.5 mt-1 outline-none focus:border-forest" data-testid={`ad-onetime-${p.key}`} /></label>
            )}
            <button onClick={() => saveProduct(p)} disabled={savingKey === p.key} className="mt-3 w-full bg-forest text-bone py-1.5 text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid={`ad-save-${p.key}`}>
              {savingKey === p.key ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
          </div>
        ))}
      </div>

      {/* Advertisements */}
      <h3 className="font-serif text-lg text-forest mt-8 mb-2">Advertisements ({data.advertisements.length})</h3>
      <div className="space-y-2" data-testid="ad-admin-list">
        {data.advertisements.map((ad) => (
          <div key={ad.id} className="bg-white border border-saddle/20 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="ad-admin-row">
            <div className="flex-1 min-w-0">
              <strong className="text-forest">{ad.product_name}{ad.state ? ` · ${ad.state}` : ""}</strong>
              <span className="block text-xs text-charcoal/50">{ad.business_name || (ad.setup && ad.setup.business_name) || "—"} · {dollars(ad.amount_cents)} · {ad.billing_cycle}</span>
            </div>
            {ad.status === "pending_review" && <button onClick={() => setStatus(ad, "active")} className="text-[11px] font-semibold text-[#2f7d4f] border border-[#2f7d4f]/40 px-2 py-1 inline-flex items-center gap-1" data-testid="ad-approve"><Check size={12} /> Approve</button>}
            <select value={ad.status} onChange={(e) => setStatus(ad, e.target.value)} className="border border-saddle/25 px-2 py-1.5 text-sm outline-none" data-testid="ad-status-select">
              {AD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
        {data.advertisements.length === 0 && <p className="text-charcoal/50 text-sm">No advertisements yet.</p>}
      </div>

      {/* Brand partnership inquiries */}
      {data.inquiries.length > 0 && (
        <>
          <h3 className="font-serif text-lg text-forest mt-8 mb-2">Brand partnership inquiries ({data.inquiries.length})</h3>
          <div className="space-y-2" data-testid="ad-inquiries">
            {data.inquiries.map((q) => (
              <div key={q.id} className="bg-white border border-saddle/20 px-4 py-3" data-testid="ad-inquiry-row">
                <strong className="text-forest">{q.company_name}</strong>
                <span className="block text-xs text-charcoal/50">{q.contact_name} · {q.email} · {q.phone || "—"}</span>
                {q.message && <p className="text-sm text-charcoal/70 mt-1">{q.message}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
